
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserProfile } from '../types';
import { api } from '../services/api';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

interface AuthContextType {
  user: UserProfile | null;
  login: (userData: UserProfile, token?: string) => void;
  updateUser: (updates: any) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Sesión persistente: mientras haya un usuario guardado en localStorage,
    // se restaura automáticamente al abrir la app (en web y en la APK), sin
    // pedir login de nuevo. Solo se borra con un logout explícito.
    const storedUser = localStorage.getItem('nexus_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);

        // SYNC WITH SERVER: Ensure plan/points are fresh
        const sync = async () => {
          const res = await api.syncProfile(parsedUser.id || parsedUser.email);
          if (res.success && res.user) {
            setUser(res.user);
            localStorage.setItem('nexus_user', JSON.stringify(res.user));
          }
        };
        sync();

      } catch (e) {
        console.error("Failed to parse user from storage");
      }
    }
    setIsLoading(false);
  }, []);

  const login = (userData: UserProfile, token?: string) => {
    setUser(userData);
    localStorage.setItem('nexus_user', JSON.stringify(userData));
    if (token) {
      localStorage.setItem('nexus_token', token);
    }
  };

  // Devuelve true/false según si el guardado en el SERVIDOR realmente
  // funcionó — antes esto siempre actualizaba el estado local (y con eso,
  // la UI se veía "guardada") sin importar si la llamada al backend falló,
  // porque api.updateProfile atrapa sus propios errores de red y nunca
  // relanza la excepción, así que el catch de acá nunca se activaba. Un
  // fallo silencioso del servidor (ej. auth.php respondiendo un error)
  // quedaba invisible hasta que localStorage se perdía (reinstalar la app)
  // y volvía a aparecer el dato viejo, porque nunca había llegado a la BD.
  const updateUser = async (updates: any): Promise<boolean> => {
    const fullUpdate = { ...user, ...updates };
    let serverOk = false;
    try {
      const res = await api.updateProfile(fullUpdate);
      serverOk = !!res && !res.error;
      if (!serverOk) console.error("updateProfile devolvió error:", res?.error);
    } catch (e) {
      console.error("Failed to update user in DB", e);
    }

    // El estado local se actualiza igual (para que la app siga funcionando
    // sin conexión), pero el resultado real se le informa a quien llamó.
    setUser((prev: any) => {
      const newUser = { ...prev, ...updates };
      localStorage.setItem('nexus_user', JSON.stringify(newUser));
      return newUser;
    });
    return serverOk;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('nexus_user');
    localStorage.removeItem('nexus_token');
    // Cierra también la sesión nativa de Google/Firebase: si no se hace esto,
    // el SDK puede reengancharse en silencio a la misma cuenta en el próximo
    // login. Al cerrarla, la próxima vez vuelve a mostrar el selector de cuenta
    // y permite elegir una distinta.
    FirebaseAuthentication.signOut().catch(() => {});
  };

  return (
    <AuthContext.Provider value={{ user, login, updateUser, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
